/**
 * Phase 3.42 — Finanzen-Section: Pfand/Refi + Treuhandkonto + Rechnungen.
 *
 * MVP-Anlage ohne echte Zahlungsflüsse — Stripe Connect Integration folgt
 * sobald die Live-Keys vom User bereitstehen.
 */
import { useEffect, useState } from "react";
import { CircleDollarSign, Landmark, FileText, Coins, Loader2, Send, CheckCircle2, ShieldAlert, RefreshCw, Building2, BadgeEuro, Wallet, FileSignature, Receipt, Plus, Trash2, TrendingUp, CreditCard } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { CheckRow } from "./_shared";
import BranchOverrideToggle from "./BranchOverrideToggle";
import { TariffSection, LegalSection } from "../DealerSettings";
import { WalletSection } from "./WalletSection";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";

const STATUS_LABEL = {
    submitted: { label: "Eingereicht", cls: "bg-blue-100 text-blue-800" },
    reviewing: { label: "In Prüfung", cls: "bg-amber-100 text-amber-800" },
    approved: { label: "Genehmigt", cls: "bg-emerald-100 text-emerald-800" },
    active: { label: "Aktiv", cls: "bg-violet-100 text-violet-800" },
    released: { label: "Freigegeben", cls: "bg-gray-100 text-gray-700" },
    rejected: { label: "Abgelehnt", cls: "bg-red-100 text-red-800" },
};

function StatusBadge({ status }) {
    const s = STATUS_LABEL[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
    return <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${s.cls}`} data-testid={`pawn-status-${status}`}>{s.label}</span>;
}

function PawnRefiPanel({ currentUser }) {
    const { refresh } = useAuth();
    const role = currentUser.role;
    const [busy, setBusy] = useState(false);
    const [myRequests, setMyRequests] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [form, setForm] = useState({
        type: role === "b2b" ? (currentUser.pawn_offer_b2b_quick ? "b2b_pawn_quick" : (currentUser.pawn_offer_b2b_refi ? "b2b_refi" : "b2b_pawn_quick")) : "b2c_pawn",
        vehicle_label: "",
        amount_eur: "",
        duration_days: "",
        dealer_id: "",
        notes: "",
    });

    // Keep form.type in sync with currently enabled programs (B2B opt-ins toggled while viewing)
    useEffect(() => {
        if (role !== "b2b") return;
        const allowed = [];
        if (currentUser.pawn_offer_b2b_quick) allowed.push("b2b_pawn_quick");
        if (currentUser.pawn_offer_b2b_refi) allowed.push("b2b_refi");
        if (allowed.length === 0) return;
        if (!allowed.includes(form.type)) {
            setForm((p) => ({ ...p, type: allowed[0] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.pawn_offer_b2b_quick, currentUser.pawn_offer_b2b_refi, role]);

    const loadRequests = async () => {
        try {
            const r = await api.get("/finance/pawn-requests");
            setMyRequests(r.data.items || []);
        } catch { /* ignore */ }
    };
    const loadDealers = async () => {
        try {
            const pc = (currentUser.postal_code || "").trim();
            const q = pc ? `?postal_code=${encodeURIComponent(pc)}` : "";
            const r = await api.get(`/finance/pawn-dealers${q}`);
            setDealers(r.data.items || []);
        } catch { /* ignore */ }
    };
    useEffect(() => {
        loadRequests();
        if (role === "b2c") loadDealers();
    }, [role]);

    const flipPref = async (key, value) => {
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { [key]: value });
            await refresh();
            toast.success("Gespeichert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        if (!form.vehicle_label.trim()) { toast.error("Fahrzeug-Bezeichnung erforderlich"); return; }
        const amt = parseFloat(form.amount_eur);
        if (!amt || amt < 100) { toast.error("Bitte gültigen Betrag (≥ 100 €) eingeben"); return; }
        if (form.type === "b2c_pawn" && !form.dealer_id) { toast.error("Bitte Pfand-Händler auswählen"); return; }
        setBusy(true);
        try {
            const payload = {
                type: form.type,
                vehicle_label: form.vehicle_label.trim(),
                amount_eur: amt,
                duration_days: form.duration_days ? parseInt(form.duration_days, 10) : null,
                notes: form.notes.trim() || null,
                dealer_id: form.type === "b2c_pawn" ? form.dealer_id : null,
            };
            await api.post("/finance/pawn-requests", payload);
            toast.success("Antrag eingereicht");
            setForm({ ...form, vehicle_label: "", amount_eur: "", duration_days: "", notes: "" });
            await loadRequests();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Senden");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="pawn-refi-panel">
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="pawn-mvp-banner">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                    MVP-Anlage: Die Verpfändung/Refinanzierung wird über die Plattform vermittelt — Auszahlung & Rückzahlung erfolgen aktuell außerhalb der Plattform. Die volle Integration mit Stripe Connect erfolgt, sobald die Zahlungs-Keys bereitstehen.
                </span>
            </div>

            {/* Dealer-Opt-ins */}
            {role === "b2b" && (
                <div className="space-y-2" data-testid="pawn-dealer-optins">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-gray-700">Programm-Teilnahme</h4>
                    <CheckRow
                        label="B2C-Pfand annehmen"
                        hint="Privatkunden können ihr Fahrzeug bei Ihnen als Pfand abgeben."
                        checked={!!currentUser.pawn_accept_b2c}
                        onChange={(v) => flipPref("pawn_accept_b2c", v)}
                        disabled={busy}
                        testId="pawn-accept-b2c-checkbox"
                    />
                    <CheckRow
                        label="Kurze Einkaufsfinanzierung (Eigenfahrzeuge verpfänden)"
                        hint="Eigene Fahrzeuge bei der Plattform für Schnell-Einkäufe verpfänden."
                        checked={!!currentUser.pawn_offer_b2b_quick}
                        onChange={(v) => flipPref("pawn_offer_b2b_quick", v)}
                        disabled={busy}
                        testId="pawn-offer-b2b-quick-checkbox"
                    />
                    <CheckRow
                        label="Nebensaison-Refinanzierung"
                        hint="Eigene Fahrzeuge in der Nebensaison zur Liquiditätsschaffung refinanzieren."
                        checked={!!currentUser.pawn_offer_b2b_refi}
                        onChange={(v) => flipPref("pawn_offer_b2b_refi", v)}
                        disabled={busy}
                        testId="pawn-offer-b2b-refi-checkbox"
                    />
                </div>
            )}

            {/* Antrag stellen — Phase 3.44 Iter 2: nur sichtbar wenn B2C ODER (B2B mit aktivierten Programm-Optionen) */}
            {(role !== "b2b" || !!currentUser.pawn_offer_b2b_quick || !!currentUser.pawn_offer_b2b_refi) ? (
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="pawn-create-form">
                <h4 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">Neuen Antrag stellen</h4>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Antrags-Typ</Label>
                        <select
                            value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                            className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 bg-white text-sm"
                            data-testid="pawn-type-select"
                        >
                            {role === "b2c" && <option value="b2c_pawn">Mein Fahrzeug beim Händler verpfänden</option>}
                            {role === "b2b" && currentUser.pawn_offer_b2b_quick && <option value="b2b_pawn_quick">Eigenes Fahrzeug für kurze Einkaufsfinanzierung verpfänden</option>}
                            {role === "b2b" && currentUser.pawn_offer_b2b_refi && <option value="b2b_refi">Nebensaison-Refinanzierung</option>}
                        </select>
                    </div>
                    {form.type === "b2c_pawn" && (
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Pfand-Händler auswählen</Label>
                            {/* Phase 3.44 — Mein lokaler Pfand-Händler Vorschlag */}
                            {currentUser.postal_code && dealers.some((d) => d.distance_score > 0) && (
                                <div className="mt-1 mb-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="pawn-local-suggestions">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1.5">
                                        Mein lokaler Pfand-Händler · PLZ {currentUser.postal_code}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {dealers.filter((d) => d.distance_score > 0).slice(0, 3).map((d) => (
                                            <button
                                                key={d.id}
                                                type="button"
                                                onClick={() => setForm({ ...form, dealer_id: d.id })}
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[11px] font-bold transition-colors ${form.dealer_id === d.id ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-100"}`}
                                                data-testid={`pawn-local-suggestion-${d.id}`}
                                            >
                                                {d.company_name}
                                                {d.city && <span className="opacity-70">· {d.city}</span>}
                                                <span className="ml-1 text-[9px] opacity-70">{d.distance_score}/5</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <select
                                value={form.dealer_id}
                                onChange={(e) => setForm({ ...form, dealer_id: e.target.value })}
                                className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 bg-white text-sm"
                                data-testid="pawn-dealer-select"
                            >
                                <option value="">— bitte wählen —</option>
                                {dealers.map((d) => (
                                    <option key={d.id} value={d.id}>{d.company_name}{d.city ? ` — ${d.city}` : ""}{d.distance_score > 0 ? ` · PLZ-Nähe ${d.distance_score}/5` : ""}</option>
                                ))}
                            </select>
                            {dealers.length === 0 && (
                                <p className="text-[10px] text-gray-500 mt-1">Aktuell keine teilnehmenden Pfand-Händler verfügbar.</p>
                            )}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fahrzeug</Label>
                            <Input
                                value={form.vehicle_label}
                                onChange={(e) => setForm({ ...form, vehicle_label: e.target.value })}
                                placeholder="z.B. BMW R 1250 GS, Bj. 2022"
                                className="rounded-sm mt-1 text-sm"
                                data-testid="pawn-vehicle-input"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betrag (€)</Label>
                            <Input
                                type="number"
                                min="100"
                                step="50"
                                value={form.amount_eur}
                                onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
                                className="rounded-sm mt-1 text-sm font-mono-num"
                                data-testid="pawn-amount-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Laufzeit (Tage, optional)</Label>
                            <Input
                                type="number"
                                min="1"
                                max="730"
                                value={form.duration_days}
                                onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                                placeholder="z.B. 90"
                                className="rounded-sm mt-1 text-sm font-mono-num"
                                data-testid="pawn-duration-input"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notizen (optional)</Label>
                            <Input
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="weitere Infos"
                                className="rounded-sm mt-1 text-sm"
                                data-testid="pawn-notes-input"
                            />
                        </div>
                    </div>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                        data-testid="pawn-submit-btn"
                    >
                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Antrag einreichen
                    </Button>
                </div>
            </div>
            ) : (
                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-sm p-4 text-center text-xs text-gray-600" data-testid="pawn-create-form-disabled">
                    Aktivieren Sie oben „<strong>Kurze Einkaufsfinanzierung</strong>" oder „<strong>Nebensaison-Refinanzierung</strong>", um Anträge stellen zu können.
                </div>
            )}

            {/* Meine Anträge */}
            <div data-testid="my-pawn-requests">
                <h4 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2">Meine Anträge ({myRequests.length})</h4>
                {myRequests.length === 0 ? (
                    <div className="text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-sm text-center">
                        Noch keine Anträge eingereicht.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {myRequests.map((r) => (
                            <div key={r.id} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`pawn-row-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm">{r.vehicle_label}</div>
                                        <div className="text-xs text-gray-600">
                                            <span className="font-mono-num font-bold">{Number(r.amount_eur).toLocaleString("de-DE")} €</span>
                                            {r.duration_days ? <span> · Laufzeit {r.duration_days} Tage</span> : null}
                                            <span> · {r.type === "b2c_pawn" ? "Privatkunde" : r.type === "b2b_pawn_quick" ? "Einkaufs-Verpfändung" : "Nebensaison-Refi"}</span>
                                        </div>
                                        {r.notes && <div className="text-xs text-gray-500 italic mt-1">„{r.notes}"</div>}
                                        {r.admin_note && <div className="text-xs text-gray-700 mt-1"><strong>Admin-Notiz:</strong> {r.admin_note}</div>}
                                    </div>
                                    <StatusBadge status={r.status} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function EscrowPanel() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({ iban: "", note: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/finance/escrow");
            setData(r.data);
            if (r.data?.iban) setForm({ iban: r.data.iban, note: r.data.note || "" });
        } catch {
            setData({ status: "not_requested", balance_eur: 0 });
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/finance/escrow/request", form);
            toast.success("Antrag gesendet");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;
    const status = data.status || "not_requested";

    return (
        <div className="space-y-4" data-testid="escrow-panel">
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs">
                MVP-Anlage: Aktuell beantragen Sie ein Treuhandkonto manuell. Die volle Transaktionsabwicklung über
                Stripe Connect folgt, sobald die Live-Keys vorliegen.
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</div>
                        <div className="font-bold text-lg flex items-center gap-2" data-testid="escrow-status">
                            {status === "not_requested" && <Badge className="bg-gray-100 text-gray-700">Nicht beantragt</Badge>}
                            {status === "requested" && <Badge className="bg-amber-100 text-amber-800">Beantragt</Badge>}
                            {status === "active" && <Badge className="bg-emerald-100 text-emerald-800">Aktiv</Badge>}
                            {status === "closed" && <Badge className="bg-gray-200 text-gray-700">Geschlossen</Badge>}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kontostand</div>
                        <div className="font-display font-black text-2xl" data-testid="escrow-balance">{Number(data.balance_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</div>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">IBAN (Auszahlung)</Label>
                        <Input
                            value={form.iban}
                            onChange={(e) => setForm({ ...form, iban: e.target.value })}
                            placeholder="DE89 3704 0044 0532 0130 00"
                            className="rounded-sm mt-1 text-sm font-mono-num"
                            data-testid="escrow-iban-input"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notiz (optional)</Label>
                        <Input
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            placeholder="z.B. spezielle Wünsche"
                            className="rounded-sm mt-1 text-sm"
                            data-testid="escrow-note-input"
                        />
                    </div>
                </div>
                <Button
                    onClick={submit}
                    disabled={busy}
                    className="mt-3 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                    data-testid="escrow-submit-btn"
                >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : status === "not_requested" ? <Send className="w-4 h-4 mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {status === "not_requested" ? "Treuhandkonto anlegen" : "Aktualisieren"}
                </Button>
            </div>
        </div>
    );
}

export function DatevStammdatenPanel({ currentUser }) {
    const { refresh } = useAuth();
    const [form, setForm] = useState({
        datev_berater_nr: currentUser.datev_berater_nr || "",
        datev_mandant_nr: currentUser.datev_mandant_nr || "",
        datev_wj_beginn: currentUser.datev_wj_beginn || `${new Date().getFullYear()}-01-01`,
        datev_skr: currentUser.datev_skr || "03",
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", {
                datev_berater_nr: form.datev_berater_nr.replace(/\D/g, "").slice(0, 7) || null,
                datev_mandant_nr: form.datev_mandant_nr.replace(/\D/g, "").slice(0, 5) || null,
                datev_wj_beginn: form.datev_wj_beginn || null,
                datev_skr: form.datev_skr || null,
            });
            await refresh();
            toast.success("DATEV-Stammdaten gespeichert.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="datev-stammdaten-panel">
            <p className="text-xs text-gray-600">
                Diese Angaben werden in den <strong>DATEV-Buchungsstapel (EXTF)</strong> übernommen, damit der Import in Ihrer Steuerkanzlei ohne Nacharbeit gelingt. Sie erhalten Berater- &amp; Mandantennummer von Ihrer Kanzlei.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Beraternummer (Kanzlei)</Label>
                    <Input value={form.datev_berater_nr} onChange={(e) => set("datev_berater_nr", e.target.value)} placeholder="z. B. 1234567" inputMode="numeric" className="rounded-sm mt-1 text-sm font-mono-num" data-testid="datev-berater-nr" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Mandantennummer</Label>
                    <Input value={form.datev_mandant_nr} onChange={(e) => set("datev_mandant_nr", e.target.value)} placeholder="z. B. 5001" inputMode="numeric" className="rounded-sm mt-1 text-sm font-mono-num" data-testid="datev-mandant-nr" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Wirtschaftsjahr-Beginn</Label>
                    <Input type="date" value={form.datev_wj_beginn} onChange={(e) => set("datev_wj_beginn", e.target.value)} className="rounded-sm mt-1 text-sm" data-testid="datev-wj-beginn" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kontenrahmen (Standard)</Label>
                    <select value={form.datev_skr} onChange={(e) => set("datev_skr", e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 bg-white text-sm" data-testid="datev-skr-default">
                        <option value="03">SKR03</option>
                        <option value="04">SKR04</option>
                    </select>
                </div>
            </div>
            <Button onClick={save} disabled={busy} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider" data-testid="datev-stammdaten-save">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Speichern
            </Button>
        </div>
    );
}

function InvoicesPanel() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/users/me/invoices")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]));
    }, []);
    if (items === null) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;
    if (items.length === 0) {
        return <div className="text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-sm text-center" data-testid="invoices-empty">Noch keine Rechnungen vorhanden.</div>;
    }
    return (
        <div className="space-y-2" data-testid="invoices-list">
            {items.map((inv) => (
                <a
                    key={inv.id}
                    href={`/api/users/me/invoices/${inv.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between gap-3 bg-white border border-gray-200 hover:border-[#111827] rounded-sm p-3"
                    data-testid={`invoice-row-${inv.id}`}
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileText className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="min-w-0">
                            <div className="font-bold text-sm">Rechnung {inv.period}</div>
                            <div className="text-xs text-gray-500">{inv.status === "draft" ? "Entwurf" : inv.status === "sent" ? `versendet ${(inv.sent_at || "").slice(0, 10)}` : inv.status}</div>
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="font-mono-num font-bold">{Number(inv.total_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider">PDF</div>
                    </div>
                </a>
            ))}
        </div>
    );
}

const DEFAULT_QUICK_POSITIONS = [
    { label: "Zulassung", description: "Zulassung / Anmeldung", unit_price_net: 0, vat_rate: 19 },
    { label: "Überführung", description: "Überführung / Transport", unit_price_net: 0, vat_rate: 19 },
    { label: "HU/AU", description: "Hauptuntersuchung (HU/AU)", unit_price_net: 0, vat_rate: 19 },
    { label: "Aufbereitung", description: "Fahrzeugaufbereitung", unit_price_net: 0, vat_rate: 19 },
    { label: "Garantie", description: "Anschluss-Garantie", unit_price_net: 0, vat_rate: 19 },
];

export function InvoiceQuickPositionsPanel() {
    const [rows, setRows] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/users/me/dealer-profile")
            .then((r) => {
                const qp = r.data?.invoice_quick_positions;
                setRows(Array.isArray(qp) && qp.length ? qp : DEFAULT_QUICK_POSITIONS);
            })
            .catch(() => setRows(DEFAULT_QUICK_POSITIONS));
    }, []);

    const setRow = (i, k, v) => setRows((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addRow = () => setRows((arr) => [...arr, { label: "", description: "", unit_price_net: 0, vat_rate: 19 }]);
    const removeRow = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));
    const resetDefaults = () => setRows(DEFAULT_QUICK_POSITIONS.map((r) => ({ ...r })));

    const importFrequent = async () => {
        try {
            const r = await api.get("/dealer/invoice-positions/frequent?limit=8");
            const freq = r.data?.positions || [];
            if (!freq.length) { toast.info("Noch keine wiederkehrenden Rechnungspositionen gefunden."); return; }
            setRows((arr) => {
                const have = new Set((arr || []).map((x) => String(x.description || "").trim().toLowerCase()));
                const additions = freq
                    .filter((f) => !have.has(String(f.description || "").trim().toLowerCase()))
                    .map((f) => ({ label: f.label, description: f.description, unit_price_net: f.unit_price_net || 0, vat_rate: f.vat_rate || 19 }));
                return [...(arr || []), ...additions];
            });
            toast.success("Häufigste Positionen vorgeschlagen — prüfen & speichern.");
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschläge konnten nicht geladen werden"); }
    };

    const save = async () => {
        const cleaned = (rows || [])
            .filter((r) => String(r.label || "").trim() && String(r.description || "").trim())
            .map((r) => ({
                label: String(r.label).trim().slice(0, 40),
                description: String(r.description).trim().slice(0, 200),
                unit_price_net: Number(r.unit_price_net) || 0,
                vat_rate: Number(r.vat_rate) || 19,
            }));
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", { invoice_quick_positions: cleaned });
            toast.success("Schnellpositionen gespeichert");
            setRows(cleaned.length ? cleaned : DEFAULT_QUICK_POSITIONS);
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setSaving(false);
    };

    if (rows === null) return <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;

    return (
        <div data-testid="invoice-quick-positions-panel">
            <p className="text-xs text-gray-600 mb-3">
                Diese Vorschläge erscheinen als Schnell-Chips in der Rechnungsmaske. Hinterlege einen Standardpreis (netto),
                damit z. B. „Überführung = 149 €" mit einem Klick als Position eingefügt wird.
            </p>
            <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 px-1 mb-1">
                <div className="col-span-2">Chip-Label</div>
                <div className="col-span-5">Beschreibung (Position)</div>
                <div className="col-span-2">Preis netto €</div>
                <div className="col-span-2">USt %</div>
                <div className="col-span-1"></div>
            </div>
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quick-pos-row-${i}`}>
                        <Input value={r.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Label" className="col-span-12 md:col-span-2 rounded-sm h-9" data-testid={`quick-pos-label-${i}`} />
                        <Input value={r.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} placeholder="Beschreibung" className="col-span-12 md:col-span-5 rounded-sm h-9" data-testid={`quick-pos-desc-${i}`} />
                        <Input type="number" step="0.01" value={r.unit_price_net ?? 0} onChange={(e) => setRow(i, "unit_price_net", e.target.value)} placeholder="0,00" className="col-span-6 md:col-span-2 rounded-sm h-9" data-testid={`quick-pos-price-${i}`} />
                        <select value={r.vat_rate ?? 19} onChange={(e) => setRow(i, "vat_rate", Number(e.target.value))} className="col-span-5 md:col-span-2 h-9 border border-gray-300 rounded-sm px-2 text-sm bg-white" data-testid={`quick-pos-vat-${i}`}>
                            <option value={19}>19 %</option>
                            <option value={7}>7 %</option>
                            <option value={0}>0 %</option>
                        </select>
                        <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-gray-400 hover:text-red-600 flex justify-center" title="Entfernen" data-testid={`quick-pos-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
                <Button type="button" variant="outline" onClick={addRow} className="rounded-sm h-9" data-testid="quick-pos-add"><Plus className="w-4 h-4 mr-1" /> Position</Button>
                <Button type="button" variant="outline" onClick={importFrequent} className="rounded-sm h-9" data-testid="quick-pos-import-frequent"><TrendingUp className="w-4 h-4 mr-1" /> Aus meinen Rechnungen vorschlagen</Button>
                <Button type="button" variant="outline" onClick={resetDefaults} className="rounded-sm h-9" data-testid="quick-pos-reset">Standard wiederherstellen</Button>
                <div className="flex-1" />
                <Button type="button" onClick={save} disabled={saving} className="rounded-sm h-9 bg-[#111827] hover:bg-black text-white font-bold" data-testid="quick-pos-save">
                    {saving ? "Speichere…" : "Speichern"}
                </Button>
            </div>
        </div>
    );
}


export function FinanzenSection({ currentUser }) {
    const isDealer = currentUser?.role === "b2b";
    const [expanded, setExpanded] = useState(false);
    useSettingsExpandAll(setExpanded);
    const [open, setOpen] = useState({ bank: false, stripeconnect: false, tariff: false, datev: false, quickpos: false, wallet: false, pawn: false, escrow: false, invoices: false, legal: false });
    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
    const Header = ({ id, icon: Icon, title, hint }) => (
        <button
            type="button"
            onClick={() => toggle(id)}
            className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 text-left"
            data-testid={`finanzen-toggle-${id}`}
        >
            <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#111827]">{title}</div>
                <div className="text-[11px] text-gray-500">{hint}</div>
            </div>
            <span className={`text-gray-400 text-lg transition-transform ${open[id] ? "rotate-180" : ""}`}>⌄</span>
        </button>
    );

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="finanzen-section">
            <div className={`flex items-center ${expanded ? "border-b border-gray-200" : ""}`}>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex-1 flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors min-w-0"
                    data-testid="finanzen-toggle"
                    aria-expanded={expanded}
                >
                    <CircleDollarSign className="w-5 h-5 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-bold">Vertragsverhältnis</h2>
                        <p className="text-xs text-gray-600 mt-1">
                            Vertrags- &amp; Zahlungskonditionen: Bankverbindung, Tarif, Pfand/Refinanzierung, Treuhandkonto, Rechnungen &amp; Rechtliches.
                        </p>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
                {isDealer && (
                    <div className="pr-5 pl-2 shrink-0">
                        <BranchOverrideToggle
                            area="vertragsverhaeltnis"
                            currentUser={currentUser}
                            infoText="Vertragsverhältnis-Einstellungen (automatischer Vertragsversand, Reservierungsdauer, Finanzierungs-Link) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                            testId="vertragsverhaeltnis-branch-override"
                        />
                    </div>
                )}
            </div>
            {expanded && (
                <div data-testid="finanzen-body">
                    {isDealer && (
                        <>
                            {/* Jun-2026 (User-Wunsch): „Bankverbindung" und „Zahlungen empfangen (Stripe)"
                                verschoben nach Stammdaten → Unternehmensdaten. */}
                            <Header id="tariff" icon={BadgeEuro} title="Tarif" hint="Aktueller Tarif & Leistungsumfang" />
                            {open.tariff && (
                                <div className="p-5 border-b border-gray-200" data-testid="finanzen-tariff-body">
                                    <TariffSection currentUser={currentUser} embedded />
                                </div>
                            )}
                            {/* DATEV-Stammdaten (Jun-2026, User-Wunsch): verschoben nach
                                Stammdaten → Unternehmensdaten. */}
                            {/* Rechnungs-Schnellpositionen (Jun-2026, User-Wunsch): nur noch
                                unter „Werkstatt / Teile / Zubehör" gepflegt. */}
                        </>
                    )}
                    <Header id="wallet" icon={Wallet} title="Mein Guthaben" hint="Sparen, Anteile, Kaution, Spenden & Auszahlung — aus €Punkten" />
                    {open.wallet && (
                        <div className="p-5 border-b border-gray-200" data-testid="finanzen-wallet-body">
                            <WalletSection currentUser={currentUser} embedded />
                        </div>
                    )}
                    <Header id="pawn" icon={Coins} title="Pfand / Refinanzierung" hint="Fahrzeug verpfänden, Einkaufsfinanzierung oder Nebensaison-Refi" />
                    {open.pawn && (
                        <div className="p-5 border-b border-gray-200" data-testid="finanzen-pawn-body">
                            <PawnRefiPanel currentUser={currentUser} />
                        </div>
                    )}
                    <Header id="escrow" icon={Landmark} title="Treuhandkonto" hint="Anlage / Status / Kontostand" />
                    {open.escrow && (
                        <div className="p-5 border-b border-gray-200" data-testid="finanzen-escrow-body">
                            <EscrowPanel />
                        </div>
                    )}
                    <Header id="invoices" icon={FileText} title="Rechnungen" hint="Monatliche PDF-Rechnungen herunterladen" />
                    {open.invoices && (
                        <div className="p-5 border-b border-gray-200" data-testid="finanzen-invoices-body">
                            <InvoicesPanel />
                        </div>
                    )}
                    {/* Jun-2026 (User-Wunsch) — „Rechtliches" aus Unternehmensdaten hierher verschoben. */}
                    <Header id="legal" icon={FileSignature} title="Rechtliches" hint="AGB · Datenschutz · Spielregeln akzeptieren" />
                    {open.legal && (
                        <div className="p-5" data-testid="finanzen-legal-body">
                            <LegalSection currentUser={currentUser} embedded />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
